import styled from '@emotion/styled';
import { DefaultDisplayLocale } from '@shared/constants/localization/LocalizationConstants';
import { compareFeaturesLink, productResources } from '@shared/constants/product/ProductUtils';
import CurrentUserContainer from '@shared/hooks/state/CurrentUserContainer';
import OrganizationContainer from '@shared/hooks/state/OrganizationContainer';
import { PurchaseOption } from '@shared/models/components/ProductDisplayModel';
import { getLanguageOnly } from '@shared/models/localization/Locales';
import { OrganizationModelLicenseKeys } from '@shared/models/organizations/OrganizationModelLicenses';
import { ProductModel } from '@shared/models/product/ProductModel';
import { CommonIntermediateResourceKey } from '@shared/models/ResourceKeys';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import Banner from '../core/alertNotification/Banner';
import { TabButton } from '../core/button/CommonButtonStyles';
import Button from '../core/button/CoreButton';
import { CardSmallGrid, CardStretchLayout } from '../core/card/CommonCardLayouts';
import ActivateLicenseCardCenteredContent from '../license/ActivateLicenseCardCenteredContent';
import ProductCard, { IProductCardProps } from './ProductCard';

interface IExploreProductsCardProps {
    variant?: 'default' | 'small';
}

// todo: add sdk payment link once that is available
// todo: purchase options are hard coded for monthly and yearly, change this if there are more options in the future
export default function ExploreProductsCard({ variant = 'default' }: IExploreProductsCardProps) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { organizationId } = useParams();
    const BodyComponent = variant == 'default' ? CardStretchLayout : CardSmallGrid;
    const organizationContainer = OrganizationContainer.useContainer();
    const currentUserContainer = CurrentUserContainer.useContainer();
    const [publicProducts, setPublicProducts] = useState<ProductModel[]>([]);
    const [purchaseOption, setPurchaseOption] = useState<'monthly' | 'yearly'>('monthly');
    const locale = currentUserContainer.userLocalePreferencesRef.current.defaultLocale;

    useEffect(() => {
        if (!currentUserContainer.hasNoOrgMemberships && !organizationId) {
            // user is part of an org, and is on the generic upgrade page meant for free users
            const isSingleOrgMember =
                currentUserContainer.userLicenseAccessRef.current?.organizationLicenses?.length === 1;

            let upgradePath = '';
            if (isSingleOrgMember) {
                upgradePath = `/org/${organizationContainer.membershipsRef.current[0]?.organization.id}/billing/redirect/upgrade`;
            } else {
                upgradePath = '/org';
            }

            // force user to upgrade with an associated org to avoid unnecessary creation of multiple orgs
            navigate(upgradePath, { replace: true });
            return;
        }

        const fetchProducts = async () => {
            const products = await organizationContainer.loadAvailableProducts();
            setPublicProducts(products);
        };
        fetchProducts();
    }, []);

    const getPurchaseOption = (licenseType: OrganizationModelLicenseKeys, subscription: 'monthly' | 'yearly') => {
        const locale = currentUserContainer.userLocalePreferencesRef.current.defaultLocale;

        const products = publicProducts.filter((p) => {
            // when checking for locale, display locale english is "en" and payment default is "en-us", hence only checking for language right now
            return (
                p[licenseType] &&
                p.isPublic &&
                (p.locale === locale ||
                    getLanguageOnly(p.locale) === getLanguageOnly(locale) ||
                    getLanguageOnly(p.locale) === getLanguageOnly(DefaultDisplayLocale))
            );
        });

        if (products.length === 0) {
            return undefined;
        }

        // sorted by billing cycle, so that the default payment link is always the first one
        const sortedProducts = products.sort((a, b) => (a.billingCycle ?? '').localeCompare(b.billingCycle ?? ''));

        const purchaseOption: PurchaseOption = {
            price: sortedProducts[0].price,
            defaultPaymentLink: sortedProducts[0].defaultPaymentLink,
            type: subscription,
            productId: sortedProducts[0].id,
        };

        if (subscription === 'yearly' && sortedProducts.length > 1) {
            purchaseOption.price = sortedProducts[1].price;
            purchaseOption.defaultPaymentLink = sortedProducts[1].defaultPaymentLink;
            purchaseOption.productId = sortedProducts[1].id;
        }

        return purchaseOption;
    };

    const getTranslatedFeatures = (featureKey: CommonIntermediateResourceKey): string[] => {
        const translatedFeatures = t(featureKey, { returnObjects: true, lng: locale });
        const jsonTranslatedCommands = JSON.parse(JSON.stringify(translatedFeatures));
        const features: string[] = [];
        for (const key in jsonTranslatedCommands) {
            features.push(jsonTranslatedCommands[key]);
        }
        return features;
    };

    const products: IProductCardProps[] = [
        {
            product: productResources.isPlusAccess,
            supportedFeatures: [
                {
                    tag: t('common:billing.isPlusAccess.supportedFeatures.tag'),
                    features: getTranslatedFeatures('common:billing.isPlusAccess.supportedFeatures.features'),
                },
            ],
            $variant: 'small',
        },
        {
            product: productResources.isEmployeeAccess,
            supportedFeatures: [
                {
                    tag: t('common:billing.isEmployeeAccess.supportedFeatures.tag'),
                    features: getTranslatedFeatures('common:billing.isEmployeeAccess.supportedFeatures.features'),
                },
            ],
            statusText: t('common:billing.mostPopular'),
        },
        {
            product: productResources.isDeveloperAccess,
            supportedFeatures: [
                {
                    tag: t('common:billing.isDeveloperAccess.supportedFeatures.tag'),
                    features: getTranslatedFeatures('common:billing.isDeveloperAccess.supportedFeatures.features'),
                },
            ],
        },
    ];

    return (
        <>
            <TabContainer role="tablist" aria-label={t('common:billing.subscription.title')}>
                <TabButton
                    role="tab"
                    aria-selected={purchaseOption === 'monthly'}
                    onClick={() => setPurchaseOption('monthly')}
                    isActive={purchaseOption === 'monthly'}>
                    {t('common:billing.subscription.monthly')}
                </TabButton>
                <TabButton
                    role="tab"
                    aria-selected={purchaseOption === 'yearly'}
                    onClick={() => setPurchaseOption('yearly')}
                    isActive={purchaseOption === 'yearly'}>
                    {t('common:billing.subscription.yearly')}
                </TabButton>
            </TabContainer>
            <BodyComponent>
                {products.map((product, i) => (
                    <ProductCard
                        key={'product_' + i}
                        {...product}
                        purchaseOption={getPurchaseOption(
                            product.product.licenseType as OrganizationModelLicenseKeys,
                            purchaseOption
                        )}
                        organizationId={organizationId}
                    />
                ))}
                <ActivateLicenseCardCenteredContent />
            </BodyComponent>
            <CenterWrapper>
                <Button href={compareFeaturesLink} endIcon="externalLink" target="_blank">
                    {t('common:learnMore')}
                </Button>
            </CenterWrapper>
        </>
    );
}

export const CenterWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 1rem 0;
`;

const TabContainer = styled(CenterWrapper)`
    display: flex;
    gap: 10px;
    margin: 1rem 0;
`;
